// "Just do it" agent task card under a chat message. A task from chat (the
// model's start_agent_task hand-off or the /agent command) drafts a plan of
// checkpoints, the user edits and approves it with a budget, then the same
// card follows the run live and ends with the agent's report and files.
// Nothing runs until the user presses Start. Module scope — App-level inner
// components remount on every render.
import { useEffect, useRef, useState } from "react";
import {
  BUDGETS, POLL_MS, MAX_STEPS, money, isTerminal, shouldAutoPlan, firstSentence, toDraft, draftToPlan,
  stepMoves, progressOf, statusLine, latestActivity, finalReport, deliverableFiles, fileUrl,
  resultSnapshot, reportBlocks, fileLabel,
} from "./agent_task_utils.js";

const AMBER = "#d97706";
const auth = (t) => ({ Authorization: `Bearer ${t}` });
const jsonHeaders = (t) => ({ ...auth(t), "Content-Type": "application/json" });

function btn(S, kind) {
  const base = { padding: "7px 14px", borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };
  if (kind === "primary") return { ...base, border: "none", background: S.accent, color: "#fff" };
  if (kind === "link") return { ...base, padding: "7px 6px", border: "none", background: "transparent", color: S.text2 };
  return { ...base, border: `1px solid ${S.border2}`, background: S.inputBg, color: S.text };
}
const iconBtn = (S) => ({ width: 24, height: 24, borderRadius: 6, border: `1px solid ${S.border2}`, background: "transparent", color: S.text3, cursor: "pointer", fontSize: 12, lineHeight: "20px", padding: 0, fontFamily: "inherit", flexShrink: 0 });
// Sizes a step box to its text. Passed as a fresh inline ref so it re-runs
// on every render (typing, width changes and font loads via PlanReview's refit state).
const growToFit = (el) => { if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + 2 + "px"; } };
const sectionLabel = (S) => ({ fontSize: 10.5, fontWeight: 800, letterSpacing: ".05em", textTransform: "uppercase", color: S.text3, margin: "12px 0 6px" });

function Shell({ S, label, right, children }) {
  return (
    <div className="cf-agent-task" style={{ padding: 14, borderRadius: 12, border: `1px solid ${S.accent}55`, background: S.cardBg, maxWidth: 560 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
        <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: S.accent }}>{label}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function GoalText({ goal, S }) {
  const [open, setOpen] = useState(false);
  const long = String(goal || "").length > 220;
  return (
    <div style={{ fontSize: 12, color: S.text2, lineHeight: 1.55, whiteSpace: "pre-wrap", marginTop: 4 }}>
      {open || !long ? goal : String(goal).slice(0, 220) + "…"}
      {long && <button onClick={() => setOpen(!open)} style={{ ...btn(S, "link"), padding: "0 4px", fontSize: 11.5 }}>{open ? "Less" : "More"}</button>}
    </div>
  );
}

// ── Before the run: draft → review → start ──────────────────────────────
function PlanView({ task, authToken, S, showToast, onUpdate }) {
  const [planning, setPlanning] = useState(false);
  const [draft, setDraft] = useState(() => (task.plan ? toDraft(task.plan) : null));
  const [budget, setBudget] = useState(task.budget || 500);
  const [starting, setStarting] = useState(false);
  const asked = useRef(false);
  const toast = (m) => showToast && showToast(m);

  const plan = async () => {
    setPlanning(true);
    try {
      const r = await fetch("/api/hermes/plan", { method: "POST", headers: jsonHeaders(authToken), body: JSON.stringify({ goal: task.goal }) });
      const d = await r.json().catch(() => ({}));
      if (d && d.enabled === false) throw new Error("Hermes isn't enabled on this workspace — ask your admin.");
      if (!r.ok || !d.plan) throw new Error(d.error || "Couldn't draft a plan (HTTP " + r.status + ")");
      setDraft(toDraft(d.plan));
      setBudget(d.budget_cents || 500);
      onUpdate({ plan: d.plan, budget: d.budget_cents || 500, dismissed: false });
    } catch (e) { toast(e.message); }
    setPlanning(false);
  };
  useEffect(() => {
    if (!asked.current && shouldAutoPlan(task)) { asked.current = true; plan(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    const p = draftToPlan(draft);
    if (!p.steps.length) { toast("Add at least one step to the plan"); return; }
    setStarting(true);
    try {
      const r = await fetch("/api/hermes/runs", { method: "POST", headers: jsonHeaders(authToken), body: JSON.stringify({ goal: task.goal, plan: p, dollar_cap_cents: budget }) });
      const d = await r.json().catch(() => ({}));
      if (d && d.enabled === false) throw new Error("Hermes isn't enabled on this workspace — ask your admin.");
      if (!r.ok || !d.id) throw new Error(d.error || d.message || "Couldn't start the task (HTTP " + r.status + ")");
      // Record the run first: if /start fails the card still offers Start.
      onUpdate({ runId: d.id, plan: d.plan || p, budget });
      const s = await fetch(`/api/hermes/runs/${encodeURIComponent(d.id)}/start`, { method: "POST", headers: auth(authToken) });
      if (!s.ok) { const e = await s.json().catch(() => ({})); throw new Error(e.error || "The task was created but didn't start — press Start to retry."); }
      toast("Hermes is on it ✓");
    } catch (e) { toast(e.message); }
    setStarting(false);
  };

  const title = (draft && draft.title) || task.title || firstSentence(task.goal);

  if (!draft) {
    return (
      <Shell S={S} label="Agent task">
        <div style={{ fontSize: 13.5, fontWeight: 700, color: S.text, margin: "4px 0 2px" }}>{title}</div>
        <GoalText goal={task.goal} S={S} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {planning
            ? <span className="cf-saving" style={{ fontSize: 12, color: S.text2 }}>Drafting a plan for you to check</span>
            : <>
                <button onClick={plan} style={btn(S, "primary")}>Plan this task</button>
                {!task.dismissed && <button onClick={() => onUpdate({ dismissed: true })} style={btn(S, "link")}>Not now</button>}
                <span style={{ fontSize: 10.5, color: S.text3 }}>{task.dismissed ? "Set aside." : "You'll see the plan before anything runs."}</span>
              </>}
        </div>
      </Shell>
    );
  }

  return (
    <Shell S={S} label="Agent task · check the plan">
      <PlanReview draft={draft} setDraft={setDraft} budget={budget} setBudget={setBudget} S={S} titlePlaceholder={title}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          <button onClick={start} disabled={starting} style={{ ...btn(S, "primary"), opacity: starting ? 0.6 : 1 }}>{starting ? "Starting…" : draft.feasible ? "Start task" : "Start anyway"}</button>
          <button onClick={plan} disabled={planning} style={btn(S)}>{planning ? "Re-planning…" : "Re-plan"}</button>
          <button onClick={() => { setDraft(null); onUpdate({ plan: null, dismissed: true }); }} style={btn(S, "link")}>Not now</button>
        </div>
      </PlanReview>
    </Shell>
  );
}

// Plan editor shared by the chat card and the Agent tasks page: title,
// questions, checkpoints (edit / reorder / remove / add), what you'll get,
// approvals and budget. The caller's action buttons come in as children.
export function PlanReview({ draft, setDraft, budget, setBudget, S, titlePlaceholder = "", children }) {
  // Step boxes size to their text on render; re-render when the list's width
  // changes (window, sidebar, panes) or the web font arrives, so a box
  // measured early doesn't clip its last line.
  const [, setRefit] = useState(0);
  const listRef = useRef(null);
  useEffect(() => {
    const refit = () => setRefit((n) => n + 1);
    let lastW = 0, ro = null;
    if (typeof ResizeObserver !== "undefined" && listRef.current) {
      ro = new ResizeObserver((entries) => {
        const w = Math.round(entries[0].contentRect.width);
        if (w !== lastW) { lastW = w; refit(); }
      });
      ro.observe(listRef.current);
    } else window.addEventListener("resize", refit);
    if (typeof document !== "undefined" && document.fonts && document.fonts.ready) document.fonts.ready.then(refit).catch(() => {});
    return () => { if (ro) ro.disconnect(); else window.removeEventListener("resize", refit); };
  }, []);
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setStep = (i, title) => set({ steps: draft.steps.map((s, j) => (j === i ? { title, detail: s.title === title ? s.detail : "" } : s)) });
  const input = { width: "100%", boxSizing: "border-box", padding: "6px 8px", borderRadius: 7, border: `1px solid ${S.border2}`, background: S.inputBg, color: S.text, fontSize: 12.5, fontFamily: "inherit" };

  return (
    <div className="cf-plan-review">
      <input aria-label="Task title" value={draft.title} onChange={(e) => set({ title: e.target.value })} placeholder={titlePlaceholder}
        style={{ ...input, fontSize: 13.5, fontWeight: 700, border: "1px solid transparent", background: "transparent", padding: "4px 0", margin: "2px 0" }} />
      {draft.summary && <div style={{ fontSize: 12, color: S.text2, lineHeight: 1.55 }}>{draft.summary}</div>}
      {!draft.feasible && (
        <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, border: `1px solid ${AMBER}66`, color: S.text, fontSize: 12, lineHeight: 1.5 }}>
          <b style={{ color: AMBER }}>Hermes may not be able to do this.</b> {draft.reason}
        </div>
      )}
      {draft.fallback && <div style={{ marginTop: 6, fontSize: 11.5, color: S.text3 }}>Couldn't draft a detailed plan, so here's a basic one. Edit the steps if you like.</div>}

      {draft.questions.length > 0 && <>
        <div style={sectionLabel(S)}>Quick questions (optional)</div>
        {draft.questions.map((q, i) => (
          <label key={i} style={{ display: "block", marginBottom: 8 }}>
            <div style={{ fontSize: 12, color: S.text, marginBottom: 4 }}>{q.q}</div>
            <input value={q.a} onChange={(e) => set({ questions: draft.questions.map((x, j) => (j === i ? { ...x, a: e.target.value } : x)) })}
              placeholder="Your answer, or leave blank and Hermes will assume" style={input} />
          </label>
        ))}
      </>}

      <div style={sectionLabel(S)}>Checkpoints</div>
      <ol ref={listRef} className="cf-agent-steps" style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
        {draft.steps.map((s, i) => (
          <li key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 20, height: 20, borderRadius: 10, background: S.accentDim, color: S.accent, fontSize: 11, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</span>
            {/* Wrapping, self-sizing box: step text stays readable in narrow chat bubbles. */}
            <textarea aria-label={"Step " + (i + 1)} rows={1} value={s.title} title={s.detail || ""}
              ref={(el) => growToFit(el)} onChange={(e) => setStep(i, e.target.value.replace(/\n/g, " "))}
              onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
              style={{ ...input, resize: "none", overflow: "hidden", lineHeight: 1.4, display: "block" }} />
            <button className="cf-agent-move" aria-label="Move up" onClick={() => set({ steps: stepMoves(draft.steps, i, -1) })} disabled={i === 0} style={{ ...iconBtn(S), opacity: i === 0 ? 0.35 : 1 }}>↑</button>
            <button className="cf-agent-move" aria-label="Move down" onClick={() => set({ steps: stepMoves(draft.steps, i, 1) })} disabled={i === draft.steps.length - 1} style={{ ...iconBtn(S), opacity: i === draft.steps.length - 1 ? 0.35 : 1 }}>↓</button>
            <button aria-label="Remove step" onClick={() => set({ steps: draft.steps.filter((_, j) => j !== i) })} style={iconBtn(S)}>✕</button>
          </li>
        ))}
      </ol>
      {draft.steps.length < MAX_STEPS && (
        <button onClick={() => set({ steps: [...draft.steps, { title: "", detail: "" }] })} style={{ ...btn(S, "link"), padding: "6px 0", color: S.accent }}>+ Add a step</button>
      )}

      {draft.deliverables.length > 0 && <>
        <div style={sectionLabel(S)}>You'll get</div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: S.text, lineHeight: 1.6 }}>{draft.deliverables.map((d, i) => <li key={i}>{d}</li>)}</ul>
      </>}
      <div style={sectionLabel(S)}>Asks you first</div>
      <div style={{ fontSize: 12, color: S.text2, lineHeight: 1.55 }}>
        {draft.approvals.length > 0 && <ul style={{ margin: "0 0 4px", paddingLeft: 18, color: S.text }}>{draft.approvals.map((d, i) => <li key={i}>{d}</li>)}</ul>}
        Anything that submits, sends, pays or deletes waits for your OK.
      </div>

      <div style={sectionLabel(S)}>Budget</div>
      <div role="radiogroup" aria-label="Budget" style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        {BUDGETS.map((c) => (
          <button key={c} role="radio" aria-checked={budget === c} onClick={() => setBudget(c)}
            style={{ padding: "5px 11px", borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", border: `1px solid ${budget === c ? S.accent : S.border2}`, background: budget === c ? S.accentDim : "transparent", color: budget === c ? S.accent : S.text2 }}>{money(c)}</button>
        ))}
        <span style={{ fontSize: 10.5, color: S.text3 }}>It stops when it reaches this.</span>
      </div>
      {children}
    </div>
  );
}

// ── During and after the run ─────────────────────────────────────────────
const MARK = { done: "✓", blocked: "!", skipped: "–", pending: "" };

function Checkpoints({ plan, current, S }) {
  const steps = plan && Array.isArray(plan.steps) ? plan.steps : [];
  if (!steps.length) return null;
  const color = (s) => (s.status === "done" ? S.success : s.status === "blocked" ? AMBER : S.text3);
  return (
    <ol className="cf-agent-steps" style={{ listStyle: "none", margin: "10px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 7 }}>
      {steps.map((s) => {
        const live = current === s.n;
        return (
          <li key={s.n} data-status={s.status} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <span aria-hidden="true" className={live ? "cf-pulse-slow" : undefined}
              style={{ width: 18, height: 18, marginTop: 1, borderRadius: 9, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800,
                border: `1.5px solid ${live ? S.accent : color(s)}`, color: s.status === "pending" ? S.text3 : "#fff", background: s.status === "pending" ? (live ? S.accentDim : "transparent") : color(s) }}>
              {MARK[s.status] || ""}
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, color: s.status === "skipped" ? S.text3 : S.text, fontWeight: live ? 700 : 500 }}>
                {s.title}<span className="sr-only">{" — " + (live ? "in progress" : s.status === "pending" ? "not done" : s.status)}</span>
              </div>
              {s.note && <div style={{ fontSize: 11.5, color: s.status === "blocked" ? AMBER : S.text3, lineHeight: 1.45 }}>{s.note}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Report({ text, S }) {
  if (!text) return null;
  return (
    <div className="cf-agent-report" style={{ marginTop: 12, padding: "10px 12px", borderRadius: 10, background: S.surface2 || S.inputBg, fontSize: 12.5, color: S.text, lineHeight: 1.55 }}>
      {reportBlocks(text).map((b, i) => (b.blank ? <div key={i} style={{ height: 6 }} /> : (
        <div key={i} style={b.bullet ? { paddingLeft: 14, position: "relative" } : undefined}>
          {b.bullet && <span style={{ position: "absolute", left: 2 }}>•</span>}
          {b.runs.map((r, j) => (r.b ? <strong key={j}>{r.t}</strong> : <span key={j}>{r.t}</span>))}
        </div>
      )))}
    </div>
  );
}

// "Repeat on a schedule": turns a finished task into an Automations entry
// (kind agent_task) that re-runs the same plan and budget on a schedule.
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
function ScheduleRepeat({ task, run, authToken, S, showToast, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState("weekly");
  const [day, setDay] = useState(1);
  const [time, setTime] = useState("09:00");
  const [email, setEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  if (task.scheduledAutomationId) return <span data-agent-scheduled style={{ fontSize: 11.5, color: S.success }}>Repeats on a schedule ✓ (Automations)</span>;
  if (!open) return <button onClick={() => setOpen(true)} style={btn(S)}>Repeat on a schedule</button>;
  const field = { padding: "5px 8px", borderRadius: 7, border: `1px solid ${S.border2}`, background: S.inputBg, color: S.text, fontSize: 12, fontFamily: "inherit" };
  const save = async () => {
    setBusy(true);
    try {
      const plan = (run && run.plan) || task.plan || null;
      let tz = "UTC";
      try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch {}
      const body = {
        name: String((plan && plan.title) || task.title || firstSentence(task.goal)).slice(0, 80),
        prompt: task.goal, preset, time, day: preset === "monthly" ? Math.min(28, Number(day) || 1) : Number(day), tz,
        kind: "agent_task", agentPlan: plan, agentBudgetCents: task.budget || (run && run.dollar_cap_cents) || 500, notifyEmail: email,
      };
      const r = await fetch("/api/automations", { method: "POST", headers: jsonHeaders(authToken), body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.id) throw new Error(d.error || "Couldn't schedule it (HTTP " + r.status + ")");
      onUpdate({ scheduledAutomationId: d.id });
      if (showToast) showToast("Scheduled ✓ — manage it under Automations");
      setOpen(false);
    } catch (e) { if (showToast) showToast(e.message); }
    setBusy(false);
  };
  return (
    <div data-agent-schedule style={{ width: "100%", marginTop: 8, padding: 10, borderRadius: 10, border: `1px solid ${S.border2}`, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", fontSize: 12, color: S.text2 }}>
      <span style={{ fontWeight: 700, color: S.text }}>Run it again</span>
      <select aria-label="How often" value={preset} onChange={(e) => setPreset(e.target.value)} style={field}>
        <option value="daily">every day</option>
        <option value="weekdays">every weekday</option>
        <option value="weekly">every week</option>
        <option value="monthly">every month</option>
      </select>
      {preset === "weekly" && <select aria-label="Day" value={day} onChange={(e) => setDay(Number(e.target.value))} style={field}>{DAYS.map((d, i) => <option key={d} value={i}>on {d}</option>)}</select>}
      {preset === "monthly" && <select aria-label="Day of month" value={day} onChange={(e) => setDay(Number(e.target.value))} style={field}>{Array.from({ length: 28 }, (_, i) => <option key={i} value={i + 1}>on day {i + 1}</option>)}</select>}
      <span>at</span>
      <input aria-label="Time" type="time" value={time} onChange={(e) => setTime(e.target.value || "09:00")} style={field} />
      <label style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} /> email me the report</label>
      <span style={{ flexBasis: "100%", fontSize: 11, color: S.text3 }}>Same plan and budget ({money(task.budget || (run && run.dollar_cap_cents) || 500)}) each time. Change or stop it any time under Automations.</span>
      <button onClick={save} disabled={busy} style={{ ...btn(S, "primary"), opacity: busy ? 0.6 : 1 }}>{busy ? "Saving…" : "Schedule it"}</button>
      <button onClick={() => setOpen(false)} style={btn(S, "link")}>Cancel</button>
    </div>
  );
}

function RunView({ task, authToken, S, showToast, onUpdate, onOpen }) {
  const [detail, setDetail] = useState(null);
  const [gone, setGone] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [busy, setBusy] = useState(false);
  const snap = task.result || null;
  const settled = !!(snap && isTerminal(snap.status));
  const toast = (m) => showToast && showToast(m);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (settled) return undefined;
    let dead = false, timer = null;
    const load = async () => {
      if (dead) return;
      if (typeof document === "undefined" || document.visibilityState !== "hidden") {
        try {
          const r = await fetch(`/api/hermes/runs/${encodeURIComponent(task.runId)}`, { headers: auth(authToken), cache: "no-store" });
          if (r.status === 404 || r.status === 403) { if (!dead) setGone(true); return; }
          if (r.ok) {
            const d = await r.json();
            if (dead) return;
            setDetail(d);
            if (isTerminal(d.run && d.run.status)) { onUpdate({ result: resultSnapshot(d) }); return; }
          }
        } catch { /* network blip — try again next tick */ }
      }
      if (!dead) timer = setTimeout(load, POLL_MS);
    };
    load();
    return () => { dead = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.runId, settled, authToken, tick]);

  const run = (detail && detail.run) || (snap
    ? { status: snap.status, cost_cents_used: snap.cost_cents, dollar_cap_cents: snap.dollar_cap_cents, plan: snap.plan }
    : { status: "pending", plan: task.plan, dollar_cap_cents: task.budget });
  const plan = run.plan || task.plan;
  const status = run.status;
  const prog = progressOf(plan, status);
  const done = isTerminal(status);
  const report = detail ? finalReport(detail.steps) : (snap && snap.report) || "";
  const files = detail ? deliverableFiles(detail.files) : (snap && snap.files) || [];
  const activity = detail && !done ? latestActivity(detail.steps) : "";
  const title = (plan && plan.title) || task.title || firstSentence(task.goal);

  const post = async (what) => {
    setBusy(true);
    try {
      const r = await fetch(`/api/hermes/runs/${encodeURIComponent(task.runId)}/${what}`, { method: "POST", headers: auth(authToken) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `Couldn't ${what} the task (HTTP ${r.status})`);
      if (what === "start") toast("Hermes is on it ✓");
      setTick((t) => t + 1);
    } catch (e) { toast(e.message); }
    setBusy(false);
    setConfirmStop(false);
  };
  const download = async (f) => {
    try {
      const r = await fetch(fileUrl(task.runId, f.path), { headers: auth(authToken) });
      if (r.status === 404) throw new Error("This file is no longer available (agent runs are kept for 7 days).");
      if (!r.ok) throw new Error("Download failed (HTTP " + r.status + ")");
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = url; a.download = fileLabel(f.path); document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) { toast(e.message); }
  };

  // Green only when the agent actually completed checkpoints and none are
  // open or blocked; a "finished" run that skipped everything reads amber.
  const clean = prog.total ? prog.done > 0 && !prog.open && !prog.blocked : true;
  const pill = done
    ? (status === "done" && clean ? { t: "Finished", c: S.success } : { t: status === "done" ? "Finished" : "Stopped", c: status === "done" ? AMBER : S.text3 })
    : status === "pending" ? { t: "Not started", c: S.text3 } : { t: status === "running" ? "Running" : "Paused", c: S.accent };

  return (
    <Shell S={S} label="Agent task" right={<span className="cf-agent-status" style={{ fontSize: 10.5, fontWeight: 800, color: pill.c, border: `1px solid ${pill.c}66`, borderRadius: 999, padding: "2px 8px" }}>{pill.t}</span>}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: S.text, margin: "4px 0 2px" }}>{title}</div>
      <div aria-live="polite" style={{ fontSize: 12, color: S.text2 }}>{gone && !snap ? "This task is no longer available (agent runs are kept for 7 days)." : statusLine(run, plan)}</div>
      <Checkpoints plan={plan} current={prog.current} S={S} />
      {activity && <div style={{ marginTop: 10, fontSize: 11.5, color: S.text3, display: "flex", gap: 6, alignItems: "center" }}><span className="cf-pulse-slow" style={{ width: 6, height: 6, borderRadius: 3, background: S.accent, flexShrink: 0 }} />Now: {activity}</div>}
      {done && <Report text={report} S={S} />}
      {done && !report && status === "done" && <div style={{ marginTop: 10, fontSize: 12, color: S.text3 }}>Hermes didn't write a report — open the task in Co-work to see everything it did.</div>}
      {files.length > 0 && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
          {files.map((f) => (
            <div key={f.path} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: S.text }}>
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📄 {fileLabel(f.path)}</span>
              <button onClick={() => download(f)} style={{ ...btn(S), padding: "4px 10px", fontSize: 11.5 }}>Download</button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        {status === "pending" && !gone && <button onClick={() => post("start")} disabled={busy} style={btn(S, "primary")}>Start</button>}
        {!done && status !== "pending" && !gone && (confirmStop
          ? <><span style={{ fontSize: 12, color: S.text }}>Stop this task?</span>
              <button onClick={() => post("cancel")} disabled={busy} style={{ ...btn(S), color: S.danger }}>Stop</button>
              <button onClick={() => setConfirmStop(false)} style={btn(S, "link")}>Keep going</button></>
          : <button onClick={() => setConfirmStop(true)} style={btn(S)}>Stop</button>)}
        {done && !gone && <ScheduleRepeat task={task} run={run} authToken={authToken} S={S} showToast={showToast} onUpdate={onUpdate} />}
        {!gone && <button onClick={() => onOpen && onOpen(task.runId)} style={btn(S, done ? undefined : "link")}>Open in Agent tasks</button>}
        {run.dollar_cap_cents > 0 && <span style={{ fontSize: 10.5, color: S.text3 }}>{money(run.cost_cents_used || 0)} of {money(run.dollar_cap_cents)} used</span>}
      </div>
    </Shell>
  );
}

export default function AgentTaskCard(props) {
  return props.task && props.task.runId ? <RunView {...props} /> : <PlanView {...props} />;
}
